import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/pages/client/Dashboard";

const STATUSES = ["DRAFT", "SCHEDULED", "ACTIVE", "PAUSED", "COMPLETED", "ARCHIVED"];
const CREATIVE_TYPES = ["IMAGE", "VIDEO", "GIF"];
const EMPTY = { client_id: "", name: "", manager: "", start_date: "", end_date: "", objective: "", description: "", creative_type: "IMAGE", creative_url: "", status: "DRAFT", budget: 0, notes: "" };

export default function AdminCampaigns() {
  const [campaigns, setCampaigns] = useState(null);
  const [clients, setClients] = useState([]);
  const [creatives, setCreatives] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/campaigns").then((r) => setCampaigns(r.data)).catch(() => setCampaigns([]));
  useEffect(() => {
    load();
    api.get("/admin/clients").then((r) => setClients(r.data)).catch(() => {});
    api.get("/admin/creatives").then((r) => setCreatives(r.data)).catch(() => {});
  }, []);

  const openCreate = () => { setEditing(null); setForm({ ...EMPTY, client_id: clients[0]?.id || "" }); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...EMPTY, ...c }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/admin/campaigns/${editing.id}`, form);
        toast.success("Campaign updated");
      } else {
        await api.post("/admin/campaigns", form);
        toast.success("Campaign created");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete campaign "${c.name}" and its geofences?`)) return;
    try {
      await api.delete(`/admin/campaigns/${c.id}`);
      toast.success("Campaign deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });
  const pickCreative = (id) => {
    const c = creatives.find((x) => x.id === id);
    if (c) setForm({ ...form, creative_url: c.url, creative_type: c.creative_type });
  };

  if (!campaigns) return <Skeleton className="h-64 bg-white/5" />;

  return (
    <div className="space-y-6" data-testid="admin-campaigns-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="section-label">Campaign Management</p>
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-white tracking-tight mt-1">Campaigns</h2>
        </div>
        <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-500" data-testid="create-campaign-btn">
          <Plus className="w-4 h-4 mr-2" /> Create Campaign
        </Button>
      </div>

      <div className="card-surface overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="campaigns-table">
            <thead>
              <tr className="text-left text-[10px] tracking-widest text-slate-500 uppercase border-b border-white/5 bg-secondary/20">
                <th className="px-5 py-3.5">Campaign</th><th className="px-5 py-3.5">Client</th>
                <th className="px-5 py-3.5">Manager</th><th className="px-5 py-3.5">Dates</th>
                <th className="px-5 py-3.5">Locations</th><th className="px-5 py-3.5">Status</th>
                <th className="px-5 py-3.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {campaigns.map((c) => (
                <tr key={c.id} className="text-slate-300 hover:bg-white/[0.02]" data-testid={`campaign-row-${c.id}`}>
                  <td className="px-5 py-3.5 font-semibold text-white">{c.name}</td>
                  <td className="px-5 py-3.5">{c.client_name}</td>
                  <td className="px-5 py-3.5 text-xs">{c.manager}</td>
                  <td className="px-5 py-3.5 text-xs font-mono">{c.start_date} → {c.end_date}</td>
                  <td className="px-5 py-3.5">{c.num_locations}</td>
                  <td className="px-5 py-3.5"><StatusBadge status={c.status} /></td>
                  <td className="px-5 py-3.5 text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" className="h-8 text-slate-300" onClick={() => openEdit(c)} data-testid={`edit-campaign-${c.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="ghost" className="h-8 text-red-400" onClick={() => remove(c)} data-testid={`delete-campaign-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-popover border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="campaign-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-white">{editing ? "Edit Campaign" : "Create Campaign"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="grid sm:grid-cols-2 gap-4 mt-2">
            <div>
              <Label className="text-slate-300">Client *</Label>
              <Select value={form.client_id} onValueChange={set("client_id")}>
                <SelectTrigger className="mt-1.5 bg-secondary/50 border-white/10" data-testid="campaign-form-client"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent className="bg-popover border-white/10">
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.company}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-slate-300">Campaign Name *</Label><Input required value={form.name} onChange={set("name")} className="mt-1.5 bg-secondary/50 border-white/10" data-testid="campaign-form-name" /></div>
            <div><Label className="text-slate-300">Campaign Manager</Label><Input value={form.manager} onChange={set("manager")} className="mt-1.5 bg-secondary/50 border-white/10" data-testid="campaign-form-manager" /></div>
            <div>
              <Label className="text-slate-300">Status</Label>
              <Select value={form.status} onValueChange={set("status")}>
                <SelectTrigger className="mt-1.5 bg-secondary/50 border-white/10" data-testid="campaign-form-status"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-popover border-white/10">
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-slate-300">Start Date *</Label><Input required type="date" value={form.start_date} onChange={set("start_date")} className="mt-1.5 bg-secondary/50 border-white/10" data-testid="campaign-form-start" /></div>
            <div><Label className="text-slate-300">End Date *</Label><Input required type="date" value={form.end_date} onChange={set("end_date")} className="mt-1.5 bg-secondary/50 border-white/10" data-testid="campaign-form-end" /></div>
            <div>
              <Label className="text-slate-300">Creative (from uploads)</Label>
              <Select onValueChange={pickCreative}>
                <SelectTrigger className="mt-1.5 bg-secondary/50 border-white/10" data-testid="campaign-form-creative"><SelectValue placeholder={form.creative_url ? "Creative selected" : "Select creative"} /></SelectTrigger>
                <SelectContent className="bg-popover border-white/10">
                  {creatives.map((c) => <SelectItem key={c.id} value={c.id}>{c.original_name} ({c.creative_type})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-slate-300">Budget (ZAR)</Label><Input type="number" min="0" value={form.budget} onChange={set("budget")} className="mt-1.5 bg-secondary/50 border-white/10" data-testid="campaign-form-budget" /></div>
            <div className="sm:col-span-2"><Label className="text-slate-300">Campaign Objective</Label><Input value={form.objective} onChange={set("objective")} className="mt-1.5 bg-secondary/50 border-white/10" data-testid="campaign-form-objective" /></div>
            <div className="sm:col-span-2"><Label className="text-slate-300">Description</Label><Textarea rows={2} value={form.description} onChange={set("description")} className="mt-1.5 bg-secondary/50 border-white/10" data-testid="campaign-form-description" /></div>
            <div className="sm:col-span-2"><Label className="text-slate-300">Notes (optional)</Label><Textarea rows={2} value={form.notes} onChange={set("notes")} className="mt-1.5 bg-secondary/50 border-white/10" data-testid="campaign-form-notes" /></div>
            <div className="sm:col-span-2">
              <Button type="submit" disabled={saving} className="w-full bg-blue-600 hover:bg-blue-500" data-testid="campaign-form-submit">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editing ? "Save Changes" : "Create Campaign"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
